import isPropValid from '@emotion/is-prop-valid';
import { type ReactElement } from 'react';
import {
  StyleSheetManager as SCStyleSheetManager,
  __PRIVATE__,
} from 'styled-components';

import { IS_PRERENDER } from '#src/config.ts';

const { StyleSheet } = __PRIVATE__;

const STYLED_COMPONENTS_LAYER = 'styled-components';

const layerRules = (rules: string | string[]) =>
  (Array.isArray(rules) ? rules : [rules]).map(
    rule => `@layer ${STYLED_COMPONENTS_LAYER}{${rule}}`
  );

const PATCHED = Symbol.for('sorare.styleSheet.layered');

/**
 * styled-components resolves its sheet through React context, so anything
 * rendered outside a `StyleSheetManager` falls back to the library's own
 * `mainSheet` and injects unlayered. Unlayered CSS outranks every layer, so a
 * single out-of-tree component re-emitting a shared atom's class (a
 * `styled(Horizontal)` in a debug widget, say) silently overrides that atom for
 * the whole app. Patching the prototype makes the layer a property of the
 * library rather than of where a component happens to render.
 */
const layerEveryStyleSheet = () => {
  const proto = StyleSheet.prototype as typeof StyleSheet.prototype & {
    [PATCHED]?: true;
  };
  if (proto[PATCHED]) return;

  const { insertRules } = proto;
  proto.insertRules = function layeredInsertRules(
    id: string,
    name: string,
    rules: string | string[]
  ) {
    insertRules.call(this, id, name, layerRules(rules));
  };
  proto[PATCHED] = true;
};

layerEveryStyleSheet();

function shouldForwardProp(propName: string, target: any) {
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  return true;
}

type Props = {
  children: ReactElement;
  /** Inject into another document, e.g. a picture-in-picture window head. */
  target?: HTMLElement;
};

export const StyleSheetManager = ({ children, target }: Props) => (
  <SCStyleSheetManager
    disableCSSOMInjection={IS_PRERENDER}
    enableVendorPrefixes
    shouldForwardProp={shouldForwardProp}
    target={target}
  >
    {children}
  </SCStyleSheetManager>
);
